import startCase from 'lodash/startCase'

import { defaultImageQuality } from '@/lib/images/quality'

import OptimizedImage from './optimized-image'
import type { ImageUrlBuilderProps, ImageProps, StoryblokAsset } from './types'

const toUrl = (builderProps: ImageUrlBuilderProps): string => {
  const { baseUrl, width, height, quality } = builderProps
  // parse storyblok url and add parameters;
  // https://a.storyblok.com/f/45415/1200x628/e13dbaed23/best-hearing-aids-2022.jpg"
  // ["https:", "", "a.storyblok.com", "f", "45415", "1200x628", "e13dbaed23", "best-hearing-aids-2022.jpg"]
  //    0        1   2                  3     4        5            6             7
  const [, , , f, spaceId, dims, uid, name] = baseUrl.split('/')

  // Default quality to 40 if not set, skip quality filter if 100
  const qualityValue = Number(quality || defaultImageQuality)
  const qualityFilter = qualityValue === 100 ? 'filters:format(webp)' : `filters:quality(${qualityValue}):format(webp)`

  const urlParts = ['https://a.storyblok.com', f, spaceId, dims, uid, name, 'm', `${width}x${height || 0}`]
  if (qualityFilter) {
    urlParts.push(qualityFilter)
  }

  return urlParts.join('/')
}

const SbAssetImage = (props: ImageProps) => {
  // this takes in an Asset from storyblok and returns an optimized image
  const { image, imgProps, sizingProps, origin = 'SbAssetImage', tracer, ...rest } = props
  const asset = image as StoryblokAsset
  const { filename, alt, title, focus } = asset
  const [, , , , , dims, , name] = filename!.split('/')
  let [nativeWidth, nativeHeight] = dims.split('x').map((d: string) => Number(d))
  // Retain the source pixels needed by callers that explicitly request 2x
  // product media; keep the existing fallback size for other consumers.
  const dimensionLimit = Math.max(1620, sizingProps?.maxWidth || 0)
  if (nativeWidth > dimensionLimit) {
    const aspect = nativeWidth / nativeHeight
    nativeWidth = dimensionLimit
    nativeHeight = Math.round(nativeWidth / aspect)
  }
  const mergedImgProps = {
    title: title || '',
    focus,
    ...imgProps,
    alt: alt || imgProps?.alt || startCase(name.split('.')[0]),
  }

  // Blok image_quality reaches the image through sizingProps.
  const mergedSizingProps = {
    toUrl,
    ...sizingProps,
    quality: sizingProps?.quality || defaultImageQuality,
    nativeWidth,
    nativeHeight,
  }
  if (nativeWidth && nativeHeight) {
    const aspectRatio = (1.0 * nativeWidth) / nativeHeight
    if (Number.isNaN(aspectRatio)) {
      console.error(
        '[sb-asset-image] Bad aspect ratio for sb asset image: %s: nw: %o, nh: %o',
        filename,
        nativeWidth,
        nativeHeight
      )
    } else {
      Object.assign(mergedSizingProps, { aspectRatio })
    }
  }
  tracer?.trace('SbAssetImage: %o', { mergedSizingProps, mergedImgProps })
  return (
    <OptimizedImage
      {...rest}
      tracer={tracer}
      image={filename!}
      sizingProps={mergedSizingProps}
      imgProps={mergedImgProps}
      origin={origin}
    />
  )
}

export default SbAssetImage
